import { useMemo } from 'react';
import { MagnifyingGlassIcon, ClipboardDocumentListIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import type { TeamRepoScore, DiscoverySummary, DiscoveryFeatureListView, SledTeam } from '@sdd-maturity/shared-types';
import { Button } from '../components/Button';
import { TEAM_SCAN_ENABLED } from '../config/scan-flag';
import { useApi } from '../hooks/useApi';
import { useScoreMode, type ScoreMode } from '../contexts/ScoreModeContext';
import { modeMeta } from '../lib/scoreMode';
import { resolveScope, childLabel } from '../lib/scope';
import { buildScopePath, buildFeaturePath, buildRepoPath } from '../lib/hierarchy-route';
import { scoreColor, LEVEL_BAR_COLORS, LEVEL_ORDER, levelLabel, normalizeLevel } from '../lib/score';
import { countRecommendations } from '../lib/findings';
import { mostRecentTimestamp } from '../lib/utils';
import { TeamScoreHeader } from '../components/TeamScoreHeader';
import { FeatureTable } from '../components/FeatureTable';
import { RepoTable } from '../components/RepoTable';
import { StrengthsPanel } from '../components/StrengthsPanel';
import { DiscoveryScanPanel } from '../components/DiscoveryScanPanel';
import { DeliveryScanPanel } from '../components/DeliveryScanPanel';
import { HighlightCard } from '../components/HighlightCard';
import { Top10List, type Top10Row } from '../components/Top10List';
import { TeamLeaderboard } from '../components/TeamLeaderboard';
import type { RollupData } from '../lib/rollup';

/**
 * Phase view (FR-3): the standing + gaps + recommendations + strengths for the
 * selected team in one lens. The lens is derived from the route (`/discovery` vs
 * `/delivery`) via useScoreMode — there's no in-page toggle. Replaces the old
 * TeamHierarchy + TeamDetail pair and folds in the retired `/gaps` page.
 */
export function PhaseView({ node, teams }: { node: SledTeam; teams: SledTeam[] }) {
  const { mode } = useScoreMode();
  const scope = resolveScope(node, teams);

  // Multi-team scope (root / Portfolio / Stream / Train): show a phase-scoped rollup
  // instead of a dead-end. The tab always carries content so the lens stays a real
  // place at every scope. A single team renders its own phase standing.
  if (!scope.isSingleTeam) {
    return <PhaseRollup node={node} teams={teams} mode={mode} />;
  }

  // Team name + lens are shown in the page header (breadcrumb → title → tabs).
  return (
    <div data-testid={`phase-view-${mode}`}>
      <PhaseStanding teamNumber={node.number} teamName={node.name} />
    </div>
  );
}

/**
 * Standing for one team, selected by lens. Each lens is a leaf that fetches only
 * its own endpoints — no welded two-lens body, no no-op off-lens fetchers.
 */
function PhaseStanding({ teamNumber, teamName }: { teamNumber: string; teamName: string }) {
  const { mode } = useScoreMode();
  return mode === 'discovery'
    ? <DiscoveryStanding teamNumber={teamNumber} teamName={teamName} />
    : <DeliveryStanding teamNumber={teamNumber} teamName={teamName} />;
}

/**
 * Discovery lens standing for one team. Mounts only in discovery mode, so it
 * fetches its Discovery summary + feature scores unconditionally.
 */
function DiscoveryStanding({ teamNumber, teamName }: { teamNumber: string; teamName: string }) {
  const { data: discoverySummaryData, needsAuth: discoveryNeedsAuth, refetch: refetchDiscoverySummary } = useApi<DiscoverySummary>(
    (client, token) => client.getDiscoverySummary(teamNumber, token),
    [teamNumber],
  );

  const { data: discoveryFeatures, isLoading: discoveryFeaturesLoading, needsAuth: featuresNeedsAuth, refetch: refetchDiscoveryFeatures } = useApi<DiscoveryFeatureListView[]>(
    (client, token) => client.getDiscoveryScores(teamNumber, token),
    [teamNumber],
  );

  if (discoveryNeedsAuth || featuresNeedsAuth) {
    return (
      <div className="card mt-md" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="discovery-auth-required">
        <p className="text-lg font-semibold text-primary">Please sign in to view data</p>
        <p className="text-secondary mt-sm">Your session has expired. Use the Sign in button in the top-right corner to re-authenticate.</p>
      </div>
    );
  }

  const hasScanned = discoverySummaryData !== null && discoverySummaryData !== undefined;
  // Score-everything mode (mirrors discovery-queries.ts's team-level recompute):
  // sdd-discovery, partial-sdd, AND not-sdd features all contribute a real
  // overallScore, so any of the three counts means there's something to show.
  const hasScores = hasScanned && (discoverySummaryData.scored > 0 || discoverySummaryData.partial > 0 || discoverySummaryData.softSkipped > 0);
  const features = Array.isArray(discoveryFeatures) ? discoveryFeatures : [];

  // Team-level "last scanned" = newest timestamp across scored features and the
  // scan-history log (the latter covers scanned-but-no-scorable-features teams,
  // where `features` is empty but a scan did run).
  const discoveryLastScanned = mostRecentTimestamp([
    ...features.map((f) => f.scanTimestamp ?? f.timestamp),
    ...(discoverySummaryData?.scanHistory ?? []).map((s) => s.timestamp),
  ]);

  // Scan status line (replaces the old /scan tab + toggle), mirroring the
  // Repo/Feature drill-down "Last scan: … · Re-scan" subtitle. Right-aligned
  // under the feature table on the populated page; left-aligned as a CTA in
  // the onboarding/empty states. On completion, refetch so scores update.
  const renderScanPanel = (align?: 'left' | 'right') => (
    <DiscoveryScanPanel
      teamNumber={teamNumber}
      teamName={teamName}
      lastScannedAt={discoveryLastScanned}
      onScanComplete={() => { refetchDiscoverySummary(); refetchDiscoveryFeatures(); }}
      align={align}
    />
  );

  if (!hasScanned && !discoveryFeaturesLoading) {
    return (
      <>
        <div className="card mt-md" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="discovery-empty">
          <MagnifyingGlassIcon aria-hidden="true" style={{ width: 32, height: 32, display: 'block', margin: '0 auto 12px', color: 'var(--genai-sem-text-secondary)' }} />
          <p className="text-lg font-semibold mb-sm">No Discovery scores yet for {teamName}</p>
          {TEAM_SCAN_ENABLED && (
            <p className="text-secondary" style={{ maxWidth: '440px', margin: '0 auto' }}>
              Use the <strong>Scan</strong> section below to score your team's Rally Features against the 6-dimension Discovery rubric.
            </p>
          )}
        </div>
        {TEAM_SCAN_ENABLED && renderScanPanel()}
      </>
    );
  }

  if (hasScanned && !hasScores) {
    const softSkipped = discoverySummaryData?.softSkipped ?? 0;
    const totalFound = (discoverySummaryData?.scored ?? 0) + (discoverySummaryData?.partial ?? 0) + softSkipped;
    const noFeaturesAtAll = totalFound === 0;
    return (
      <>
      <div className="card mt-md" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="discovery-no-sdd">
        <ClipboardDocumentListIcon aria-hidden="true" style={{ width: 32, height: 32, display: 'block', margin: '0 auto 12px', color: 'var(--genai-sem-text-secondary)' }} />
        <p className="text-lg font-semibold mb-sm">
          {noFeaturesAtAll
            ? `No Rally Features found for ${teamName} in the last 30 days`
            : `No scorable Rally Features for ${teamName}`}
        </p>
        <p className="text-secondary" style={{ maxWidth: '520px', margin: '0 auto 16px' }}>
          {noFeaturesAtAll ? (
            <>
              The scanner looks at Rally <strong>Portfolio Features</strong> updated in the last 30 days
              (user stories don't count). If the team is actively delivering, check that work is rolled up
              under a Feature in Rally — and that Feature sits in one of the team's own Rally projects,
              named <strong>{teamName}</strong> followed by its delivery stream (for example{' '}
              <strong>{teamName} - WM</strong>).
            </>
          ) : (
            <>
              Found <strong>{totalFound}</strong> feature{totalFound === 1 ? '' : 's'} but{' '}
              {softSkipped === totalFound
                ? 'none had enough content for the model to score against the 6-dimension Discovery rubric'
                : "the model couldn't score them against the Discovery rubric"}
              . A useful Feature description includes a problem statement, scope, dependencies, and acceptance criteria.
            </>
          )}
        </p>
        {TEAM_SCAN_ENABLED && (
          <p className="text-secondary text-sm">Re-scan from the <strong>Scan</strong> section below.</p>
        )}
      </div>
      {TEAM_SCAN_ENABLED && renderScanPanel()}
      </>
    );
  }

  const scored = discoverySummaryData?.scored ?? 0;
  const partial = discoverySummaryData?.partial ?? 0;
  const softSkipped = discoverySummaryData?.softSkipped ?? 0;
  // overallScore now averages across all three buckets (score-everything mode,
  // mirrors discovery-queries.ts), so "has a real average" means any of the
  // three counts is nonzero, not just `scored`. A team with none of the three
  // has no real average to show — render "—" rather than the literal 0 that
  // overallScore defaults to (mirrors Overview.tsx's TeamScorecard gate).
  const total = scored + partial + softSkipped;
  const avgScore = total > 0 ? discoverySummaryData?.overallScore ?? 0 : null;

  // "Recommendations" counts dimensions carrying a recommendation across the
  // team's features — see countRecommendations.
  let recommendationsCount = 0;
  features.forEach(f => { recommendationsCount += countRecommendations(f.dimensions); });

  return (
    <div className="mt-md" data-testid="discovery-content">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--genai-sem-accent, #5bb7c8)', lineHeight: 1.2 }}>{total}</div>
          <div className="text-base text-muted" style={{ marginTop: '4px' }}>Features Scored</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: avgScore === null ? 'var(--genai-sem-text-muted)' : scoreColor(avgScore), lineHeight: 1.2 }}>{avgScore ?? '—'}</div>
          <div className="text-base text-muted" style={{ marginTop: '4px' }}>Average Score</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--genai-sem-accent, #5bb7c8)', lineHeight: 1.2 }}>
            {recommendationsCount}
          </div>
          <div className="text-base text-muted" style={{ marginTop: '4px' }}>Recommendations</div>
        </div>
      </div>

      {discoveryFeaturesLoading ? (
        <div className="card" style={{ padding: 'var(--genai-space-lg)' }}>
          <p className="text-secondary">Loading features...</p>
        </div>
      ) : (
        <FeatureTable features={features} />
      )}

      {/* Scan status line tucked under the table's bottom-right. */}
      <div className="mt-sm">{TEAM_SCAN_ENABLED && renderScanPanel('right')}</div>

      <div className="mt-lg">
        <StrengthsPanel mode="discovery" />
      </div>
    </div>
  );
}

/**
 * Delivery lens standing for one team. Mounts only in delivery mode, so it
 * fetches its repo scores unconditionally.
 */
function DeliveryStanding({ teamNumber, teamName }: { teamNumber: string; teamName: string }) {
  const { data, isLoading, error, refetch } = useApi<{ repos: TeamRepoScore[] }>(
    (client, token) => client.getTeamRepos(teamNumber, token),
    [teamNumber],
  );

  const scannedRepos = useMemo(
    () => (data?.repos ?? []).filter((r) => r.lastScanTimestamp || r.overallLevel === 'SKIPPED' || r.framework === 'stale'),
    [data],
  );

  // Scan status line (replaces the old /scan tab + toggle), mirroring the
  // Repo/Feature drill-down "Last scan: … · Re-scan" subtitle. Right-aligned
  // under the repo table on the populated page; left-aligned as a CTA in the
  // empty state. On completion, refetch so new repo scores appear in place.
  const renderDeliveryScanPanel = (align?: 'left' | 'right') => (
    <DeliveryScanPanel
      teamNumber={teamNumber}
      teamName={teamName}
      lastScannedAt={mostRecentTimestamp(scannedRepos.map((r) => r.lastScanTimestamp))}
      onScanComplete={refetch}
      align={align}
    />
  );

  // Only blank to the loading card on the INITIAL load (no data yet). A refetch()
  // — e.g. the one DeliveryScanPanel fires on scan completion — flips isLoading
  // back to true; unmounting the content subtree here would tear down the scan
  // panel + TeamScanProgress, which then remount and re-detect "complete",
  // re-firing refetch — an infinite reload loop (#711). Keeping the stale data
  // mounted during a refetch makes it non-destructive and breaks that cycle.
  if (isLoading && !data) {
    return (
      <div className="card mt-md" style={{ padding: 'var(--genai-space-lg)' }} data-testid="repos-loading">
        <p className="text-secondary">Loading repos for {teamName}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card mt-md" style={{ padding: 'var(--genai-space-lg)', borderColor: 'var(--genai-sem-error)' }} data-testid="repos-error">
        <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>Failed to load repos</p>
        <p className="text-secondary mt-sm">{error}</p>
        <Button variant="secondary" className="mt-md" onClick={refetch}>Retry</Button>
      </div>
    );
  }

  if (scannedRepos.length === 0) {
    return (
      <>
        <div className="card mt-md" style={{ padding: 'var(--genai-space-lg)', textAlign: 'center' }} data-testid="repos-empty">
          <p className="text-lg font-semibold">{teamName}</p>
          <p className="text-secondary mt-sm">
            No scanned repositories found for this team.
            {TEAM_SCAN_ENABLED && <> Use the <strong>Scan</strong> section below to get started.</>}
          </p>
        </div>
        {TEAM_SCAN_ENABLED && renderDeliveryScanPanel()}
      </>
    );
  }

  return (
    <div className="mt-md" data-testid="repos-content">
      <TeamScoreHeader repos={scannedRepos} />

      {/* Scan status line tucked under the Active repos table's bottom-right,
          above the collapsed "Other repos" section. */}
      <RepoTable
        repos={scannedRepos}
        teamNumber={teamNumber}
        showComponent
        afterActive={TEAM_SCAN_ENABLED && renderDeliveryScanPanel('right')}
      />
    </div>
  );
}

/**
 * Multi-team phase rollup (Portfolio/Stream/Train scope). Leads with the rich
 * rollup modules for THIS lens — scope stat cards, an item-level maturity
 * distribution, a positive-only Highlights band, a Top-10 of the strongest
 * individual repos/features, and a per-direct-child leaderboard (every child
 * node, drillable). The rollup is the scope's own precomputed `GET /rollup/{n}`
 * response (TASK-6) — ONE fetch regardless of scope size, its leaderboard
 * already carrying each direct child's own aggregate; when it's unavailable
 * the page shows the empty state below instead (the maturity distribution
 * needs the rollup's real per-item levels). Clicking a leaderboard row drills
 * the scope down.
 */
function PhaseRollup({ node, teams, mode }: { node: SledTeam; teams: SledTeam[]; mode: ScoreMode }) {
  const meta = modeMeta(mode);

  // The scope's own rollup is the ONE fetch this view needs — its per-direct-child
  // leaderboard already feeds the leaderboard/stat-cards/distribution below (no
  // standings list is rendered here, so no per-child fan-out is needed at all).
  const { data, isLoading, error, refetch } = useApi<{ scopeName: string | null; rollup: RollupData | null }>(
    async (client, token) => {
      const scope = resolveScope(node, teams);
      if (!scope.node || scope.children.length === 0) {
        return { scopeName: scope.node?.name ?? null, rollup: null };
      }
      const rollup = await client.getRollup(scope.node.number, token).catch(() => null);
      return { scopeName: scope.node.name, rollup };
    },
    [node.number],
  );

  // The scope node's immediate children are the standings rows AND the leaderboard
  // units (Delivery Streams / Release Trains). Each leaderboard unit name links into
  // that unit's scope at the SAME lens (see buildHref below). When the scope is a
  // Release Train, those children are scrum teams — we suppress the leaderboard and
  // don't rank them, to protect scrum-team-to-scrum-team comparison.
  const scopeNode = node;
  const childLbl = childLabel(scopeNode);
  const childrenAreScrumTeams = scopeNode.tier === 3;

  const rollup = data?.rollup ?? null;
  const lensRollup = rollup ? (mode === 'discovery' ? rollup.discovery : rollup.delivery) : null;

  const accent = 'var(--genai-sem-accent, #5bb7c8)';
  // Stat cards are lens-specific: Discovery shows feature count + discovery avg,
  // Delivery shows repo count + delivery avg. The off-lens item count and average
  // are suppressed so each rollup speaks only to its own phase.
  const statCards = rollup
    ? mode === 'discovery'
      ? [
          { label: 'Teams', value: String(rollup.teamCount), color: accent },
          { label: meta.itemNounPlural, value: String(rollup.featureCount), color: accent },
          { label: meta.avgLabel, value: String(rollup.discoveryAvg), color: scoreColor(rollup.discoveryAvg) },
        ]
      : [
          { label: 'Teams', value: String(rollup.teamCount), color: accent },
          { label: meta.itemNounPlural, value: String(rollup.repoCount), color: accent },
          { label: meta.avgLabel, value: String(rollup.deliveryAvg), color: scoreColor(rollup.deliveryAvg) },
        ]
    : [];

  // Top 10 strongest individual items in this lens (repos for delivery, features for discovery).
  const top10Rows: Top10Row[] = !rollup
    ? []
    : mode === 'discovery'
      ? rollup.discovery.top10.map((r) => {
          const formattedId = r.feature.formattedId ?? r.feature.featureFormattedId ?? '';
          // Show the feature's maturity level as the same filled pill the repo
          // leaderboard uses (this is a maturity dashboard) — not the SDD/Partial/Stub
          // content bucket. Unscored features have no level → no badge.
          const level = normalizeLevel(r.feature.level);
          return {
            key: `${r.teamNumber}:${formattedId}`,
            teamName: r.teamName,
            itemName: r.feature.name ?? r.feature.formattedId ?? 'Feature',
            score: r.feature.overallScore ?? 0,
            badge: level ? <span className={`badge badge-${level.toLowerCase()}`}>{levelLabel(level)}</span> : undefined,
            // Canonical leaf URL only; without an owning team there is no valid
            // path, so the row renders as plain text (Top10List handles undefined).
            href: formattedId && r.teamNumber ? buildFeaturePath(r.teamNumber, formattedId) : undefined,
          };
        })
      : rollup.delivery.top10.map((r) => ({
          key: `${r.teamNumber}:${r.repo.orgName}/${r.repo.repoName}`,
          teamName: r.teamName,
          itemName: r.repo.repoName,
          score: r.repo.overallScore,
          badge: <span className={`badge badge-${String(r.repo.overallLevel).toLowerCase()}`}>{levelLabel(r.repo.overallLevel)}</span>,
          href: r.teamNumber ? buildRepoPath(r.teamNumber, r.repo.orgName, r.repo.repoName) : undefined,
        }));

  // Maturity distribution: the server's own per-mode `distribution`, already
  // carried on `lensRollup.levelCounts` (AC-1.1/AC-1.3) — NEVER re-derived by
  // summing `lensRollup.leaderboard[].levelCounts`. The leaderboard has one row
  // per CHILD SCOPE, and a repo/feature whose owning team is mapped under more
  // than one child scope is counted once per row there; re-summing it is the
  // exact 771-vs-897 phantom-population defect this bar must not reintroduce.
  // No fallback to that sum when `levelCounts` is absent/all-zero — an empty
  // object here correctly falls through to the empty state below (AC-1.4).
  const levelCounts = lensRollup?.levelCounts ?? {};
  const scoredCount = Object.values(levelCounts).reduce((sum, n) => sum + n, 0);
  const levels = LEVEL_ORDER.filter((l) => levelCounts[l]);
  const distributionLabel = `${meta.itemNoun} Maturity Distribution`;

  // The standings list below holds this scope's immediate children, labelled by
  // the child tier (a Portfolio's rows are Delivery Streams, a Stream's are Trains).

  return (
    <div data-testid={`phase-rollup-${mode}`}>
      {/* Scope + lens are shown in the page header (breadcrumb → title → tabs). */}
      {isLoading ? (
        <div className="card" style={{ padding: 'var(--genai-space-lg)' }} data-testid="phase-rollup-loading">
          <p className="text-secondary">Loading {meta.label} standings…</p>
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 'var(--genai-space-lg)', borderColor: 'var(--genai-sem-error)' }} data-testid="phase-rollup-error">
          <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>Failed to load standings</p>
          <p className="text-secondary mt-sm">{error}</p>
          <Button variant="secondary" className="mt-md" onClick={refetch}>Retry</Button>
        </div>
      ) : (
        <>
          {statCards.length > 0 && (
            <div data-testid="rollup-stat-cards" style={{ display: 'grid', gridTemplateColumns: `repeat(${statCards.length}, 1fr)`, gap: '12px', marginBottom: '16px' }}>
              {statCards.map((s) => (
                <div key={s.label} data-testid="rollup-stat-card" className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
                  <p style={{ fontSize: '28px', fontWeight: 700, color: s.color, lineHeight: 1.2 }}>{s.value}</p>
                  <span className="text-base text-muted">{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {scoredCount > 0 && (
            <div className="card" style={{ padding: '16px 12px', marginBottom: '16px' }} data-testid="phase-rollup-distribution">
              <div style={{ display: 'flex', height: '24px', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                {levels.map((lvl) => (
                  <div key={lvl} style={{ width: `${((levelCounts[lvl] ?? 0) / scoredCount) * 100}%`, backgroundColor: LEVEL_BAR_COLORS[lvl], minWidth: '20px', transition: 'width 0.3s ease' }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 'var(--genai-space-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
                {levels.map((lvl) => (
                  <span key={lvl} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--genai-text-base)' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: LEVEL_BAR_COLORS[lvl], flexShrink: 0 }} />
                    <span style={{ color: 'var(--genai-sem-text-muted)' }}>{levelLabel(lvl)}</span>
                    <strong style={{ color: LEVEL_BAR_COLORS[lvl] }}>{levelCounts[lvl]}</strong>
                  </span>
                ))}
              </div>
              <span className="text-base text-muted" style={{ textAlign: 'center', marginTop: '4px', display: 'block' }}>{distributionLabel}</span>
            </div>
          )}

          {lensRollup && lensRollup.highlights.length > 0 && (
            <div data-testid="rollup-highlights" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
              {lensRollup.highlights.map((h) => (
                <HighlightCard key={`${h.kind}:${h.itemName}`} highlight={h} />
              ))}
            </div>
          )}

          {rollup && (
            <div style={{ display: 'grid', gridTemplateColumns: childrenAreScrumTeams ? '1fr' : '1fr 1fr', gap: '16px', alignItems: 'start', marginBottom: '16px' }}>
              <Top10List
                title={meta.top10Title}
                itemHeader={meta.itemHeader}
                rows={top10Rows}
              />
              {!childrenAreScrumTeams && (
                <TeamLeaderboard
                  rows={lensRollup?.leaderboard ?? []}
                  title={`${childLbl.singular} leaderboard`}
                  unitColLabel={childLbl.singular}
                  unitLabel={meta.itemNounPlural}
                  buildHref={(unit) => buildScopePath(unit, mode)}
                />
              )}
            </div>
          )}

          {!rollup && (
            <div className="card" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="phase-rollup-empty">
              <ChartBarIcon aria-hidden="true" style={{ width: 32, height: 32, display: 'block', margin: '0 auto 12px', color: 'var(--genai-sem-text-secondary)' }} />
              <p className="text-lg font-semibold mb-sm">{`No ${meta.label} rollup yet for ${data?.scopeName ?? node.name}`}</p>
              <p className="text-secondary" style={{ maxWidth: '460px', margin: '0 auto' }}>
                {`Once the ${childLbl.plural.toLowerCase()} under this scope have been scored for ${meta.label}, their rollup — stat cards, maturity distribution, and leaderboard — will appear here. Drill into a team to run a scan.`}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
